import { feedCadence } from "app/utility/SuggestFeeds.js"

import { inputCredHandle, postInputEndpoint } from "@/lib/input-api"
import { networkFromServiceCode } from "@/lib/network-service-codes"
import { type NetworkSlug } from "@/lib/networks"
import { initialsFromName } from "@/shadcn/components/NewAutomation/OutputTile"
import {
  type ConnectedSource,
  type PostPreviewSpec,
} from "@/shadcn/components/NewAutomation/types"

/**
 * The bridge from `POST /input/preview` to the post-preview step.
 *
 * The endpoint answers with the source's single most recent item, in a shape
 * that differs per input type (`feedTitle`/`itemImage` for a feed,
 * `displayName`/`mediaLink` for a social account, `description` for the video
 * networks). `ReviewPanel` already flattens those into one record for the
 * legacy review panel; this does the same for the new flow, so the two screens
 * can't disagree about which field is the headline.
 *
 * Deliberately NOT here: engagement counts and follower numbers. The design's
 * cards have slots for them and the sample data fills them in, but the endpoint
 * returns none — inventing plausible ones would make a preview look like a
 * report of a post that already happened.
 */

/**
 * Every field any `preview_api` implementation returns, all optional: the union
 * across InputFeed, InputFacebook, InputInstagramBusiness, InputTikTok and
 * InputYoutube. An input with no recent item answers `[]`, so even the item
 * fields of the matching type can be absent.
 */
export type InputPreviewPayload = {
  /** INPUT_TYPE_FEED */
  feedTitle?: string
  feedUrl?: string
  feedImage?: string
  itemImage?: string
  /**
   * 7-day item rate, computed from the feed this call just parsed. Feed inputs
   * only — the social preview_api implementations have no equivalent, so their
   * chips carry no cadence.
   */
  inputVelocity?: number | string | null
  /** Social accounts. */
  displayName?: string
  userName?: string
  profileUrl?: string
  avatarUrl?: string
  mediaLink?: string
  mediaType?: string
  /** YouTube / TikTok. */
  imageLink?: string
  description?: string
  embedHtml?: string
  /** Shared item fields. */
  itemTitle?: string
  itemBody?: string
  itemLink?: string
  itemDate?: string
}

/** The input record's fields this file reads. Legacy data, so all optional. */
export type InputRecord = {
  name?: string
  type?: number | string
  subtype?: number | string | null
  url?: string
  inputId?: number | string
  scID?: number | string
  credID?: number | string
  attrs?: { playlistID?: string | number }
  /**
   * 7-day item rate, attached by RouteNew when FEED_VALIDATE returns it. A
   * number from the API; typed loosely because everything else on this record is
   * legacy data of uncertain type.
   */
  inputVelocity?: number | string | null
}

type Constants = Record<string, number>

function toInt(value: number | string | null | undefined): number {
  return parseInt(String(value ?? ""), 10)
}

/**
 * The `POST /input/preview` body for an input, or null when the input carries
 * no identifier the endpoint can resolve.
 *
 * Null is a normal outcome, not a failure: a subtype feed that has not been
 * validated yet has no `inputId`, and the endpoint would answer 400. Callers
 * skip the request and show the sample card instead.
 */
export function inputPreviewRequest(
  input: InputRecord | null | undefined,
  constants: Constants
): Record<string, string | number> | null {
  if (!input || !input.type) return null

  const type = toInt(input.type)
  const request: Record<string, string | number> = { type }

  switch (type) {
    case constants.INPUT_TYPE_FEED:
      if (!input.inputId) return null
      request.inputId = toInt(input.inputId)
      break

    case constants.INPUT_TYPE_FACEBOOK_PAGE:
    case constants.INPUT_TYPE_INSTAGRAM_BUSINESS:
    case constants.INPUT_TYPE_TIKTOK:
    case constants.INPUT_TYPE_YOUTUBE: {
      // The preview runs before the route is created, so a reused social cred
      // or the OAuth return is usually all that identifies the account.
      const handle = inputCredHandle(input)
      if (!handle) return null
      Object.assign(request, handle)

      if (input.attrs?.playlistID !== undefined) {
        request.playlistID = String(input.attrs.playlistID)
      }
      break
    }

    default:
      return null
  }

  return request
}

/** Fetches the preview payload, or resolves null when the input can't be previewed. */
export async function fetchInputPreview(
  apiUrl: string,
  input: InputRecord | null | undefined,
  constants: Constants
): Promise<InputPreviewPayload | null> {
  const request = inputPreviewRequest(input, constants)
  if (!request) return null

  const data = await postInputEndpoint(apiUrl, "preview", request)

  // An input with no recent item answers `[]` rather than an object, because
  // PHP encodes an empty array as one. Normalise it to an empty record so
  // callers get "no item" rather than an array they'd index into.
  if (!data || Array.isArray(data)) return {}

  return data as InputPreviewPayload
}

/** Bare hostname of the first usable URL, `www.` dropped. "" when none parse. */
function hostFrom(...urls: Array<string | undefined>): string {
  for (const url of urls) {
    if (!url) continue
    try {
      return new URL(url).hostname.replace(/^www\./, "")
    } catch {
      // A relative or malformed URL is routine in feed data.
    }
  }
  return ""
}

/**
 * Compact relative time, e.g. "2h" — the form the design's cards print.
 *
 * Returns undefined rather than guessing when the date doesn't parse: feed
 * dates arrive in whatever format the publisher chose, and a wrong timestamp on
 * a preview reads as a real one.
 */
export function relativeTime(date: string | undefined): string | undefined {
  if (!date) return undefined

  const then = Date.parse(date)
  if (!Number.isFinite(then)) return undefined

  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 0) return undefined
  if (seconds < 60) return "now"

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h`

  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d`

  const weeks = Math.round(days / 7)
  if (days < 365) return `${weeks}w`

  return `${Math.round(days / 365)}y`
}

/** True when the payload's `mediaLink` is a video rather than a still. */
function isVideoMedia(payload: InputPreviewPayload): boolean {
  return Boolean(payload.mediaLink) && /video/i.test(payload.mediaType ?? "")
}

/**
 * The still for the post card's media block, or the video's poster frame.
 *
 * `itemImage` (feed) and `imageLink` (YouTube, TikTok) are always stills. Meta's
 * `mediaLink` is whatever the post itself carried, so it is a still only when
 * `mediaType` doesn't say video — a video there belongs in `previewVideoUrl`,
 * and in an `<img>` it would be a broken image rather than a player.
 */
export function previewMediaUrl(
  payload: InputPreviewPayload | null | undefined
): string | undefined {
  if (!payload) return undefined

  if (payload.itemImage) return payload.itemImage
  if (payload.imageLink) return payload.imageLink
  if (payload.mediaLink && !isVideoMedia(payload)) return payload.mediaLink

  return undefined
}

/**
 * A playable video for the media block — a Meta reel or video post.
 *
 * Only Facebook and Instagram return one: YouTube and TikTok answer with a
 * thumbnail plus, for YouTube, an `embedHtml` iframe that the card deliberately
 * does not render. Those two therefore preview as a still, which is what the
 * legacy review panel showed for them too.
 */
export function previewVideoUrl(
  payload: InputPreviewPayload | null | undefined
): string | undefined {
  if (!payload || !isVideoMedia(payload)) return undefined

  return payload.mediaLink
}

/** True when the payload actually carries an item rather than just the account. */
export function hasPreviewItem(
  payload: InputPreviewPayload | null | undefined
): boolean {
  return Boolean(payload && (payload.itemTitle || payload.itemBody || payload.description))
}

/*
 * Two captions, because the card means two different things.
 *
 * REAL_NOTE runs when the fetch returned the user's latest item: the content is
 * theirs and only the per-network chrome around it is simulated.
 *
 * SAMPLE_NOTE runs when it didn't. That card is fabricated end to end, and its
 * wording is the only thing saying so — keep it disclaiming.
 */
const SAMPLE_NOTE = "Sample preview — actual formatting varies by network"
const REAL_NOTE = "Sample previews - most recent item"

/**
 * The post card's content for an input, with or without a fetched item.
 *
 * With no item the card still renders: this step runs before destinations are
 * chosen, so for a user with nothing connected yet its job is to show what an
 * automation produces at all. `note` is what keeps that honest — the sample
 * wording says outright that the content isn't theirs yet.
 */
export function postPreviewFromPayload(
  payload: InputPreviewPayload | null | undefined,
  input: InputRecord | null | undefined,
  constants: Constants
): PostPreviewSpec {
  const data = payload ?? {}
  const author = data.feedTitle || data.displayName || input?.name || "Your source"
  const type = toInt(input?.type)
  const social = type !== constants.INPUT_TYPE_FEED

  // The title is the headline of the link the post carries; the body is the
  // descriptive copy above it. That split matches SOC_OVRD_TEXT_BOTH ("Title +
  // Description"), the default post text for a new input.
  const headline = data.itemTitle || undefined
  const body = data.itemBody || data.description || data.itemTitle

  return {
    author: String(author),
    initials: initialsFromName(String(author)),
    avatarUrl: data.feedImage || data.avatarUrl || undefined,
    handle: social && data.userName ? `@${data.userName}` : undefined,
    body: body || "Your next published item will appear here.",
    headline,
    mediaUrl: previewMediaUrl(data),
    videoUrl: previewVideoUrl(data),
    linkDomain: hostFrom(data.itemLink, data.feedUrl, input?.url, data.profileUrl) || undefined,
    timeAgo: relativeTime(data.itemDate),
    // Chrome of the simulated Facebook card rather than data — a page post is
    // public. Nothing here reports a real audience setting.
    audience: "Public",
    note: hasPreviewItem(data) ? REAL_NOTE : SAMPLE_NOTE,
  }
}

/** The source chip's record for an input being previewed. */
export function connectedSourceFromInput(
  payload: InputPreviewPayload | null | undefined,
  input: InputRecord | null | undefined,
  constants: Constants
): ConnectedSource {
  const name = String(
    payload?.feedTitle || payload?.displayName || input?.name || "Your source"
  )

  return {
    name,
    monogram: name.charAt(0).toUpperCase(),
    // The subtype is the brand for the feed-backed sources (Etsy, Shopify,
    // Substack, Pinterest, WordPress); the type is only right without one.
    network:
      networkFromServiceCode(input?.subtype || input?.type) ?? undefined,
    iconUrl: payload?.feedImage || payload?.avatarUrl || undefined,
    /*
     * Payload first, input record second — the same order as the name and icon
     * above, and for the same reason: the payload is fetched for THIS screen,
     * while the input record only carries a velocity if it is still the object
     * FEED_VALIDATE mutated. The record is kept as a fallback because a source
     * whose preview can't be resolved (no inputId yet) has no payload at all.
     *
     * Through prod's own formatter rather than a second copy of the rules: a feed
     * at the clamp floor reports the same 0.1 whether it is dead or simply has no
     * history, which is why feedCadence renders nothing there instead of
     * "~0 updates / week". `|| undefined` keeps the chip's separator from
     * appearing before an empty string.
     */
    velocity:
      feedCadence(payload?.inputVelocity ?? input?.inputVelocity) || undefined,
  }
}

/**
 * The networks the post preview shows, for everyone.
 *
 * Deliberately NOT narrowed to the accounts the user has connected. The step
 * runs before destinations are picked and its job is to show what an automation
 * produces at all — so a publisher with nothing connected sees the same four as
 * one with twenty accounts, which is the point of showing it this early. These
 * are also the four the design draws a real per-platform card for; every other
 * network falls back to a generic one.
 */
export const POST_PREVIEW_NETWORKS: NetworkSlug[] = [
  "facebook",
  "x",
  "linkedin",
  "instagram",
]
